/**
 * 내선 관리(7단계) 화면용 API 선택기 + 로컬 목(fixture).
 *
 * call-api 7단계(전화기·설치 장소·내선 생명주기)가 서버에 오기 전에 화면을 눈으로 확인하려고
 * 둔다. **비운영 빌드(`NODE_ENV !== 'production'`)에서 `?pbxMock=1` 을 붙였을 때만** 목을
 * 쓰고(세션 동안 유지, `?pbxMock=0` 으로 끈다), 그 밖에는 항상 실제 callbotApi 다 — 운영
 * 빌드(`build-prod`)는 NODE_ENV 가 production 이라 이 분기를 타지 않는다. `serve`·`build-dev`
 * 도 development 라 목이 켜질 수 있으므로, 켜진 동안 화면 헤더에 `목 데이터` 배지를 보인다.
 *
 * 목 데이터는 정본 계약 필드명 그대로다. 서버가 배포되면 이 파일과 선택기를 뗀다.
 * 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "요청 계약"
 * admin 이식: ~/md/front/admin-콜봇-내선관리-화면-요청.md "요청 계약 3"
 *
 * 시그니처: callbotApi 와 1:1 — 모든 메서드의 첫 인자는 `companyId` 다(목은 무시한다).
 * 컴포넌트는 usePbxInventory 가 companyId 를 바인딩한 `inventory.api` 로만 부른다.
 */
import { callbotApi } from '@/views/Callbot/api';

const MOCK_FLAG_KEY = 'admin.pbxMock';

/** 비운영 빌드 + `?pbxMock=1`(세션 유지) 일 때만 true */
export function isPbxMockEnabled() {
  if (process.env.NODE_ENV === 'production') return false;
  try {
    const q = new URLSearchParams(window.location.search).get('pbxMock');
    if (q === '1') sessionStorage.setItem(MOCK_FLAG_KEY, '1');
    if (q === '0') sessionStorage.removeItem(MOCK_FLAG_KEY);
    return sessionStorage.getItem(MOCK_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

/** 내선 관리 화면은 항상 이걸로 API 를 얻는다 */
export function getPbxApi() {
  return isPbxMockEnabled() ? pbxMockApi : callbotApi;
}

// ---------------------------------------------------------------------------
// 목 구현 — 메모리 상태. 새로고침하면 초기값으로 돌아간다.
// ---------------------------------------------------------------------------

const MIN = 60 * 1000;

function mockError(status, code, message, details) {
  const err = new Error(message);
  err.isAxiosError = true;
  err.response = { status, data: { error: { code, message, details } } };
  return err;
}

function delay(value, ms = 250) {
  return new Promise((resolve) => setTimeout(() => resolve(structuredClone(value)), ms));
}

function randomToken(len, alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789') {
  let s = '';
  for (let i = 0; i < len; i += 1) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return s;
}

const PROV_BASE = 'https://intest-call-api.certi.life';

function initialState() {
  const now = Date.now();
  const site = {
    id: 'site-1',
    name: '사무실',
    status: 'active',
    sip_server_fqdn: 'dev-pbx.certi.life',
    sip_transport_default: 'tls',
    sip_port_default: 5061,
    srtp_require: false,
    tls_verify_wildcard_cert: true,
    ntp_server: 'time.google.com',
    gmt_offset_sec: 32400,
    polling_period_sec: 300,
    public_ips: ['203.0.113.10'],
    restrict_provisioning_to_site_ips: false,
    token_version: 1,
    previous_token_valid_until: null,
    unmigrated_device_count: 0,
    created_at: now - 3 * 24 * 60 * MIN,
    updated_at: now - 3 * 24 * 60 * MIN,
  };
  const counselorNumbers = [
    cn('cn-1001', '데스크 1', '1001', 'ext-1001', 'legacy', 'n/a', 'reachable'),
    cn('cn-1003', '데스크 3', '1003', 'ext-1003', 'active', 'synced', 'reachable', 'dev-1003'),
    cn('cn-1006', '상담실 A', '1006', 'ext-1006', 'active', 'synced', 'unregistered', 'dev-1006'),
    cn('cn-1007', '상담실 B', '1007', 'ext-1007', 'disabled', 'synced', 'unregistered'),
    cn('cn-1008', '상담실 C', '1008', 'ext-1008', 'locked', 'synced', 'unregistered'),
  ];
  const reg = (status, observedAgo) => ({
    status,
    transport: status === 'reachable' ? 'tls' : null,
    contact_host_masked: status === 'reachable' ? '203.0.x.x' : null,
    user_agent: status === 'reachable' ? 'PolycomVVX-VVX_150-UA/6.4.3.5018' : null,
    observed_at: now - observedAgo,
    source: 'event',
    rtt_ms: status === 'reachable' ? 12 : null,
  });
  const extensions = [
    {
      id: 'ext-1008', number: '1008', status: 'locked', origin: 'issued', sync_state: 'synced',
      last_error_code: null, counselor_number_id: 'cn-1008', registration: reg('unregistered', 60 * MIN),
      device_id: null, secret_rotated_at: null, disabled_at: null, rotation: null, checked_at: now - 2 * MIN,
    },
    {
      id: 'ext-1007', number: '1007', status: 'disabled', origin: 'issued', sync_state: 'synced',
      last_error_code: null, counselor_number_id: 'cn-1007', registration: reg('unregistered', 30 * MIN),
      device_id: null, secret_rotated_at: null, disabled_at: now - 30 * MIN, rotation: null, checked_at: now - 2 * MIN,
    },
    {
      id: 'ext-1006', number: '1006', status: 'active', origin: 'issued', sync_state: 'synced',
      last_error_code: null, counselor_number_id: 'cn-1006', registration: reg('unregistered', 5 * MIN),
      device_id: 'dev-1006', secret_rotated_at: null, disabled_at: null, rotation: null, checked_at: now - 2 * MIN,
    },
    {
      id: 'ext-1005', number: '1005', status: 'retired', origin: 'issued', sync_state: 'synced',
      last_error_code: null, counselor_number_id: null, registration: null,
      device_id: null, secret_rotated_at: null, disabled_at: null, rotation: null, checked_at: null,
    },
    {
      id: 'ext-1004', number: '1004', status: 'active', origin: 'issued', sync_state: 'error',
      last_error_code: 'PBX_AMI_TIMEOUT', counselor_number_id: null, registration: null,
      device_id: null, secret_rotated_at: null, disabled_at: null, rotation: null, checked_at: now - 2 * MIN,
    },
    {
      id: 'ext-1003', number: '1003', status: 'active', origin: 'adopted', sync_state: 'synced',
      last_error_code: null, counselor_number_id: 'cn-1003', registration: reg('reachable', 2 * 24 * 60 * MIN),
      device_id: 'dev-1003', secret_rotated_at: now - 3 * MIN, disabled_at: null,
      rotation: {
        state: 'awaiting_phone', reason: 'manual', started_at: now - 3 * MIN, promoted_at: now - 3 * MIN + 2000,
        notify: { at: now - 3 * MIN + 2500, outcome: 'queued' },
      },
      checked_at: now - 2 * MIN,
    },
    {
      id: 'ext-1001', number: '1001', status: 'legacy', origin: 'legacy', sync_state: 'n/a',
      last_error_code: null, counselor_number_id: 'cn-1001', registration: reg('reachable', 5 * 24 * 60 * MIN),
      device_id: null, secret_rotated_at: null, disabled_at: null, rotation: null, checked_at: now - 2 * MIN,
    },
  ];
  const devices = [
    device('dev-new', '482567231152', 'unassigned', null, null, {
      first_seen: { at: now - 20 * MIN, ip_masked: '203.0.x.x' },
      user_agent: 'PolycomVVX-VVX_150-UA/6.4.3.5018',
      model: 'VVX 150',
    }),
    device('dev-1003', '0004f2aa0003', 'assigned', 'ext-1003', 'cn-1003', {
      label: '데스크 3 전화기',
      model: 'VVX 150',
      first_seen: { at: now - 3 * 24 * 60 * MIN, ip_masked: '203.0.x.x' },
      last_provision: {
        at: now - 40 * MIN, ip_masked: '203.0.x.x', user_agent: 'PolycomVVX-VVX_150-UA/6.4.3.5018',
        file: '0004f2aa0003.cfg', token_version: 1,
      },
      provisioning_state: 'current',
      assigned_at: now - 3 * 24 * 60 * MIN,
      admin_password_rotated_at: now - 3 * 24 * 60 * MIN,
      recovery_code: { expires_at: now + 20 * 60 * MIN, used_at: null },
    }),
    device('dev-1006', '0004f2aa0006', 'assigned', 'ext-1006', 'cn-1006', {
      model: 'VVX 150',
      first_seen: { at: now - 10 * MIN, ip_masked: '203.0.x.x' },
      provisioning_state: 'never',
      assigned_at: now - 2 * MIN,
      sip_transport: 'udp',
      sip_port: 5060,
    }),
    device('dev-old', '0004f2aa0099', 'returned', null, null, {
      label: '고장 전화기',
      first_seen: { at: now - 30 * 24 * 60 * MIN, ip_masked: '203.0.x.x' },
    }),
  ];
  const logs = {
    'dev-1003': [
      { id: 'log-1', name: '0004f2aa0003-app.log', size: 48213, uploaded_at: now - 60 * MIN },
      { id: 'log-2', name: '0004f2aa0003-boot.log', size: 10240, uploaded_at: now - 26 * 60 * MIN },
    ],
  };
  return { site, sites: [site], counselorNumbers, extensions, devices, logs };

  function cn(id, name, number, extId, status, sync, registration, deviceId = null) {
    return {
      id,
      name,
      number,
      is_extension: true,
      pbx_extension: {
        id: extId,
        number,
        status,
        sync_state: sync,
        registration_status: registration,
        transport: registration === 'reachable' ? 'tls' : null,
        registration_observed_at: now - 5 * MIN,
        checked_at: now - 2 * MIN,
        device_id: deviceId,
      },
    };
  }

  function device(id, mac, status, extensionId, counselorNumberId, over) {
    return {
      id,
      site_id: 'site-1',
      mac,
      label: null,
      model: null,
      status,
      extension_id: extensionId,
      counselor_number_id: counselorNumberId,
      sip_transport: null,
      sip_port: null,
      srtp_require: null,
      first_seen: null,
      last_provision: null,
      admin_password_rotated_at: null,
      recovery_code: null,
      provisioning_state: 'never',
      assigned_at: null,
      created_at: now - 3 * 24 * 60 * MIN,
      updated_at: now - MIN,
      ...over,
    };
  }
}

let state = null;
function db() {
  if (!state) state = initialState();
  return state;
}

function findExtByCounselor(cnId) {
  const ext = db().extensions.find((e) => e.counselor_number_id === cnId);
  if (!ext) throw mockError(404, 'PBX_EXTENSION_NOT_FOUND', '내선을 찾을 수 없습니다.');
  return ext;
}

function findDevice(id) {
  const d = db().devices.find((x) => x.id === id);
  if (!d) throw mockError(404, 'PBX_DEVICE_NOT_FOUND', '전화기를 찾을 수 없습니다.');
  return d;
}

function findSite(id) {
  const s = db().sites.find((x) => x.id === id);
  if (!s) throw mockError(404, 'PBX_SITE_NOT_FOUND', '설치 장소를 찾을 수 없습니다.');
  return s;
}

function startRotation(ext, reason) {
  const now = Date.now();
  ext.rotation = { state: 'pending', reason, started_at: now, promoted_at: null, notify: { at: null, outcome: null } };
  ext.secret_rotated_at = now;
}

function syncSummary(ext) {
  const row = db().counselorNumbers.find((n) => n.id === ext.counselor_number_id);
  if (row?.pbx_extension) {
    row.pbx_extension = { ...row.pbx_extension, status: ext.status, device_id: ext.device_id ?? null };
  }
}

async function run(fn, ms) {
  await new Promise((r) => setTimeout(r, 200));
  return delay(fn(), ms ?? 50);
}

/** callbotApi 와 같은 시그니처 — 첫 인자 `_companyId` 는 목에서 무시한다 */
export const pbxMockApi = {
  // 내선 관리 진입 화면의 병원 목록(콜봇 조직이 있는 병원). 회사 횡단 조회라 companyId 가 없다.
  listCallbotOrganizations: () =>
    run(() => ({
      items: [
        {
          company_id: '64c9e63f84f532d3c0636868',
          company_name: '서티치과',
          vox_organization_id: 'org_mock_certi',
          key_status: 'active',
          verification_status: 'approved',
          created_at: Date.now() - 30 * 24 * 60 * 60 * 1000,
          updated_at: Date.now() - 2 * 24 * 60 * 60 * 1000,
        },
        {
          company_id: '64c9e63f84f532d3c0636869',
          company_name: '목 데이터 치과',
          vox_organization_id: 'org_mock_2',
          key_status: 'pending',
          verification_status: null,
          created_at: Date.now() - 60 * 60 * 1000,
          updated_at: Date.now() - 60 * 60 * 1000,
        },
        {
          company_id: '64c9e63f84f532d3c063686a',
          company_name: null,
          vox_organization_id: 'org_mock_orphan',
          key_status: 'active',
          verification_status: 'rejected',
          created_at: Date.now() - 10 * 24 * 60 * 60 * 1000,
          updated_at: Date.now() - 10 * 24 * 60 * 60 * 1000,
        },
      ],
    })),
  listCounselorNumbers: (_companyId) => run(() => ({ items: db().counselorNumbers })),
  listPbxExtensions: (_companyId) => run(() => ({ items: db().extensions })),
  refreshPbxExtensions: (_companyId) =>
    run(() => {
      const now = Date.now();
      db().extensions.forEach((e) => {
        if (e.status !== 'retired') e.checked_at = now;
      });
      return { items: db().extensions };
    }, 600),
  releasePbxExtension: (_companyId, extId) =>
    run(() => {
      const s = db();
      const ext = s.extensions.find((e) => e.id === extId);
      if (!ext) throw mockError(404, 'PBX_EXTENSION_NOT_FOUND', '내선을 찾을 수 없습니다.');
      if (ext.status !== 'retired' && ext.status !== 'blocked') {
        throw mockError(409, 'PBX_EXTENSION_RELEASE_NOT_ALLOWED', '회수됐거나 차단된 내선만 해제할 수 있습니다.', { reason: 'STATUS' });
      }
      s.extensions = s.extensions.filter((e) => e.id !== extId);
      return { id: ext.id, number: ext.number, released: true, released_at: Date.now() };
    }),
  syncCounselorExtension: (_companyId, cnId) => run(() => {
    const ext = findExtByCounselor(cnId);
    ext.sync_state = 'synced';
    ext.last_error_code = null;
    return ext;
  }),
  lockCounselorExtension: (_companyId, cnId) => run(() => {
    const ext = findExtByCounselor(cnId);
    ext.status = 'locked';
    ext.registration = ext.registration ? { ...ext.registration, status: 'unregistered', observed_at: Date.now() } : null;
    syncSummary(ext);
    return ext;
  }),
  unlockCounselorExtension: (_companyId, cnId, body = {}) => run(() => {
    const ext = findExtByCounselor(cnId);
    if (ext.status !== 'locked') {
      throw mockError(409, 'PBX_EXTENSION_STATE_CONFLICT', '잠기지 않은 내선은 잠금 해제할 수 없습니다.');
    }
    ext.status = 'active';
    if (!body.keep_credentials) startRotation(ext, 'unlock');
    syncSummary(ext);
    return { ...ext, warnings: [{ code: 'PBX_EXTENSION_SYNC_PENDING', detail: null }] };
  }),
  rotateCounselorExtensionSecret: (_companyId, cnId) => run(() => {
    const ext = findExtByCounselor(cnId);
    const r = ext.rotation;
    if (r && r.state !== 'confirmed' && r.state !== 'failed') {
      throw mockError(409, 'PBX_EXTENSION_STATE_CONFLICT', '진행 중인 시크릿 회전이 있어 다시 회전할 수 없습니다.', { rotation_state: r.state });
    }
    startRotation(ext, 'manual');
    return ext;
  }),
  disableCounselorExtension: (_companyId, cnId) => run(() => {
    const ext = findExtByCounselor(cnId);
    ext.status = 'disabled';
    ext.disabled_at = Date.now();
    syncSummary(ext);
    return ext;
  }),
  resumeCounselorExtension: (_companyId, cnId, body = {}) => run(() => {
    const ext = findExtByCounselor(cnId);
    ext.status = 'active';
    ext.disabled_at = null;
    if (body.rotate_secret) startRotation(ext, 'manual');
    syncSummary(ext);
    return ext;
  }),
  checkSyncCounselorExtension: (_companyId, cnId, body = {}) => run(() => {
    const ext = findExtByCounselor(cnId);
    // 목: 1003 은 통화 중으로 흉내 낸다 — 강제 확인창 경로 확인용
    if (ext.number === '1003' && !body.force) return { notified: false, contacts: 1, reason: 'EXTENSION_IN_CALL' };
    if (ext.registration?.status !== 'reachable') return { notified: false, contacts: 0, reason: 'EXTENSION_NOT_REGISTERED' };
    return { notified: true, contacts: 1, reason: null };
  }),
  listPbxDevices: (_companyId, params = {}) => run(() => ({
    items: db().devices.filter(
      (d) => (!params.status || d.status === params.status) && (!params.site_id || d.site_id === params.site_id)
    ),
  })),
  createPbxDevice: (_companyId, payload) => run(() => {
    const s = db();
    findSite(payload.site_id);
    if (s.devices.some((d) => d.mac === payload.mac)) {
      throw mockError(409, 'PBX_DEVICE_MAC_ALREADY_REGISTERED', '이미 등록된 MAC 입니다.');
    }
    const now = Date.now();
    const d = {
      id: `dev-${randomToken(6)}`, site_id: payload.site_id, mac: payload.mac, label: payload.label ?? null,
      model: null, status: 'unassigned', extension_id: null, counselor_number_id: null, sip_transport: null,
      sip_port: null, srtp_require: null, first_seen: null, last_provision: null, admin_password_rotated_at: null,
      recovery_code: null, provisioning_state: 'never', assigned_at: null, created_at: now, updated_at: now,
    };
    s.devices.unshift(d);
    return d;
  }),
  updatePbxDevice: (_companyId, id, payload) => run(() => {
    const d = findDevice(id);
    if (payload.site_id && payload.site_id !== d.site_id && d.status === 'assigned') {
      throw mockError(409, 'PBX_DEVICE_STATE_CONFLICT', '연결된 전화기는 설치 장소를 옮길 수 없습니다. 먼저 연결을 해제하세요.');
    }
    if (payload.label !== undefined) d.label = payload.label || null;
    if (payload.site_id) d.site_id = payload.site_id;
    d.updated_at = Date.now();
    return d;
  }),
  claimPbxDevice: (_companyId, id, payload) => run(() => {
    const s = db();
    const d = findDevice(id);
    if (d.status === 'assigned') throw mockError(409, 'PBX_DEVICE_ALREADY_ASSIGNED', '이미 다른 내선에 연결된 전화기입니다.');
    const ext = findExtByCounselor(payload.counselor_number_id);
    if (ext.device_id) {
      if (!payload.replace) {
        throw mockError(409, 'PBX_EXTENSION_ALREADY_ASSIGNED', '이 내선에는 이미 전화기가 연결돼 있습니다.', { device_id: ext.device_id });
      }
      const old = s.devices.find((x) => x.id === ext.device_id);
      if (old) Object.assign(old, { status: 'returned', extension_id: null, counselor_number_id: null });
      startRotation(ext, 'device_replace');
    }
    const now = Date.now();
    Object.assign(d, {
      status: 'assigned', extension_id: ext.id, counselor_number_id: payload.counselor_number_id,
      assigned_at: now, updated_at: now,
    });
    ext.device_id = d.id;
    syncSummary(ext);
    return d;
  }),
  unassignPbxDevice: (_companyId, id) => run(() => {
    const d = findDevice(id);
    const ext = db().extensions.find((e) => e.id === d.extension_id);
    if (ext) {
      ext.device_id = null;
      startRotation(ext, 'device_unassign');
      syncSummary(ext);
    }
    Object.assign(d, { status: 'unassigned', extension_id: null, counselor_number_id: null, assigned_at: null, recovery_code: null });
    return d;
  }),
  deletePbxDevice: (_companyId, id) => run(() => {
    const d = findDevice(id);
    if (d.status === 'assigned') throw mockError(409, 'PBX_DEVICE_STATE_CONFLICT', '연결된 전화기는 반납할 수 없습니다. 먼저 연결을 해제하세요.');
    d.status = 'returned';
    d.recovery_code = null;
    return undefined;
  }),
  rotatePbxDeviceAdminPassword: (_companyId, id) => run(() => {
    const d = findDevice(id);
    d.admin_password_rotated_at = Date.now();
    return d;
  }),
  getPbxDeviceAdminPassword: (_companyId, id) => run(() => {
    findDevice(id);
    return { admin_password: randomToken(10, '0123456789') };
  }),
  updatePbxDeviceTransport: (_companyId, id, payload) => run(() => {
    const d = findDevice(id);
    d.sip_transport = payload.sip_transport;
    d.sip_port = payload.sip_port ?? (payload.sip_transport === 'tls' ? 5061 : 5060);
    d.srtp_require = payload.sip_transport === 'udp' ? null : (payload.srtp_require ?? null);
    return d;
  }),
  checkSyncPbxDevice: (_companyId, id) => run(() => {
    const d = findDevice(id);
    return d.status === 'assigned'
      ? { notified: true, contacts: 1, reason: null }
      : { notified: false, contacts: 0, reason: 'EXTENSION_NOT_REGISTERED' };
  }),
  createPbxDeviceRecoveryCode: (_companyId, id, body = {}) => run(() => {
    const d = findDevice(id);
    if (d.recovery_code && d.recovery_code.expires_at > Date.now() && !d.recovery_code.used_at && !body.replace) {
      throw mockError(409, 'PBX_RECOVERY_CODE_ACTIVE', '이미 유효한 복구 코드가 있습니다.');
    }
    const code = randomToken(6, 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789');
    const expires = Date.now() + 24 * 60 * MIN;
    d.recovery_code = { expires_at: expires, used_at: null };
    return { code, expires_at: expires, recovery_url: `${PROV_BASE}/r/${code}` };
  }),
  listPbxDeviceLogs: (_companyId, id) => run(() => ({ items: db().logs[id] ?? [] })),
  downloadPbxDeviceLog: (_companyId, id, logId) =>
    run(() => new Blob([`mock log ${id}/${logId}\n`], { type: 'text/plain' })),
  listPbxSites: (_companyId) => run(() => {
    const s = db();
    return {
      items: s.sites.map((site) => ({
        ...site,
        unmigrated_device_count: s.devices.filter(
          (d) => d.site_id === site.id && d.status !== 'returned' && d.last_provision
            && (d.last_provision.token_version ?? 0) < site.token_version
        ).length,
      })),
    };
  }),
  createPbxSite: (_companyId, payload) => run(() => {
    const now = Date.now();
    const site = {
      id: `site-${randomToken(6)}`,
      name: payload.name,
      status: 'active',
      sip_server_fqdn: 'dev-pbx.certi.life',
      sip_transport_default: payload.sip_transport_default ?? 'tls',
      sip_port_default: payload.sip_port_default ?? ((payload.sip_transport_default ?? 'tls') === 'tls' ? 5061 : 5060),
      srtp_require: payload.srtp_require ?? false,
      tls_verify_wildcard_cert: true,
      ntp_server: payload.ntp_server ?? 'time.google.com',
      gmt_offset_sec: payload.gmt_offset_sec ?? 32400,
      polling_period_sec: payload.polling_period_sec ?? 300,
      public_ips: payload.public_ips ?? [],
      restrict_provisioning_to_site_ips: payload.restrict_provisioning_to_site_ips ?? false,
      token_version: 1,
      previous_token_valid_until: null,
      unmigrated_device_count: 0,
      created_at: now,
      updated_at: now,
    };
    db().sites.unshift(site);
    return { ...site, provisioning_url: `${PROV_BASE}/prov/${randomToken(32)}/` };
  }),
  updatePbxSite: (_companyId, id, payload) => run(() => {
    const site = findSite(id);
    Object.assign(site, payload, { updated_at: Date.now() });
    return site;
  }),
  deletePbxSite: (_companyId, id) => run(() => {
    const s = db();
    findSite(id);
    if (s.devices.some((d) => d.site_id === id && d.status !== 'returned')) {
      throw mockError(409, 'PBX_SITE_HAS_DEVICES', '반납되지 않은 전화기가 있어 삭제할 수 없습니다.');
    }
    s.sites = s.sites.filter((x) => x.id !== id);
    return undefined;
  }),
  rotatePbxSiteToken: (_companyId, id, body = {}) => run(() => {
    const s = db();
    const site = findSite(id);
    const unmigrated = s.devices.filter(
      (d) => d.site_id === id && d.status !== 'returned' && (d.last_provision?.token_version ?? 0) < site.token_version
    ).length;
    if (site.previous_token_valid_until && site.previous_token_valid_until > Date.now() && unmigrated > 0 && !body.revoke_previous) {
      throw mockError(409, 'PBX_SITE_ROTATION_IN_GRACE', '이전 토큰 유예가 진행 중입니다.', {
        previous_token_valid_until: site.previous_token_valid_until,
        unmigrated_device_count: unmigrated,
      });
    }
    site.token_version += 1;
    site.previous_token_valid_until = body.revoke_previous ? null : Date.now() + (body.grace_hours ?? 72) * 60 * MIN;
    return {
      provisioning_url: `${PROV_BASE}/prov/${randomToken(32)}/`,
      token_version: site.token_version,
      previous_token_valid_until: site.previous_token_valid_until,
    };
  }),
  revokePreviousPbxSiteToken: (_companyId, id) => run(() => {
    const site = findSite(id);
    site.previous_token_valid_until = null;
    return site;
  }),
  getPbxSiteProvisioningUrl: (_companyId, id) => run(() => {
    findSite(id);
    return { provisioning_url: `${PROV_BASE}/prov/${randomToken(32)}/` };
  }),
};
